import { listViewStore, getSelectedTableView } from 'owa-mail-list-store';
import { action } from 'satcheljs/lib/legacy';
import reloadTable from 'owa-mail-list-actions/lib/actions/data-loading/reloadTable';
import { now, asDate } from 'owa-datetime';
import { getStore as getSharedFavoritesStore } from 'owa-favorites';
import { FavoritePersonaData } from 'owa-favorites-types';
import folderStore from 'owa-folders';
import { isFeatureEnabled } from 'owa-feature-flags';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { MILLISECONDS_IN_MINUTE } from 'owa-date-constants';
import { logUsage } from 'owa-analytics';

let propagateUnpauseTask: NodeJS.Timer;

/**
 * Toggles paused inbox state
 */
export default action('togglePauseInbox')((pausedLength: number, actionSource: string) => {
    // If user restarted inbox, reload table to get the most up to date row information
    if (listViewStore.inboxPausedDateTime && listViewStore.inboxPausedLength === pausedLength) {
        unpauseInbox(actionSource);
    } else {
        pauseInbox(pausedLength, actionSource);

        if (pausedLength) {
            propagateUnpauseTask = setTimeout(() => {
                unpauseInbox('timer');
            }, pausedLength * MILLISECONDS_IN_MINUTE);
        }
    }
});

let unpauseInbox = action('unpauseInbox')((actionSource: string) => {
    clearTimeout(propagateUnpauseTask);
    propagateUnpauseTask = null;

    logUsage(
        'TnS_UnpauseInbox',
        [
            actionSource,
            asDate(listViewStore.inboxPausedDateTime),
            asDate(now()),
            listViewStore.inboxPausedLength ? listViewStore.inboxPausedLength : -1,
        ],
        {
            isCore: true,
        }
    );

    listViewStore.inboxPausedDateTime = null;

    const selectedTableView = getSelectedTableView();
    if (selectedTableView.tableQuery.folderId === folderNameToId('inbox')) {
        reloadTable(selectedTableView);
    }
    // Reset the pausedTotalCounts when user exits paused inbox
    getPersonaFavoriteSearchIds().forEach(searchId => {
        if (folderStore.folderTable.has(searchId)) {
            folderStore.folderTable.get(searchId).pausedTotalCount = null;
        }
    });
});

let pauseInbox = action('pauseInbox')((pausedLength: number, actionSource: string) => {
    clearTimeout(propagateUnpauseTask);
    propagateUnpauseTask = null;
    listViewStore.inboxPausedDateTime = now();
    listViewStore.inboxPausedLength = pausedLength;

    logUsage(
        'TnS_PauseInbox',
        [actionSource, asDate(listViewStore.inboxPausedDateTime), pausedLength ? pausedLength : -1],
        {
            isCore: true,
        }
    );

    // Cache current total folder counts as pausedTotalCounts for all persona favorites so we can determine
    // how many new emails there are in persona favorites later
    getPersonaFavoriteSearchIds().forEach(searchId => {
        if (folderStore.folderTable.has(searchId)) {
            folderStore.folderTable.get(searchId).pausedTotalCount = folderStore.folderTable.get(
                searchId
            ).TotalCount;
        }
    });
});

function getPersonaFavoriteSearchIds(): string[] {
    // Get favorite persona search folder ids
    const favoritesStore = getSharedFavoritesStore();
    return isFeatureEnabled('tri-favorites-roaming')
        ? [...favoritesStore.outlookFavorites.values()]
              .filter(favorite => favorite.type === 'persona')
              .map(favorite => (favorite as FavoritePersonaData).searchFolderId)
        : [...favoritesStore.favoritesPersonaNodes.values()].map(
              personaNode => personaNode.searchFolderId
          );
}
